<template>
  <div
    class="c-radio-raw"
    :class="classes"
    :style="style"
    :data-testid="dataTestId"
  >
    <p v-if="description" class="text">{{ description }}</p>

    <div class="inner">
      <div
        v-for="(option, index) in options"
        :key="index"
        :class="[ 'radio-option', { '-disabled': option.disabled } ]"
        @mouseenter="onMouseEnter(option)"
        @mouseleave="onMouseLeave(option)"
      >
        <c-icon-toggle
          ref="iconToggleRefs"
          @update:checked="onToggleUpdate(option, $event)"
          :data-testid="`option-${fieldName}-${getLabel(option)}`"
          :checked="isSelectedNative(option)"
          :disabled="disabled || option.disabled"
          :hovered="getLabel(option) === selectedLabel"
        />

        <p class="label" @click.stop="onClickLabel(option, index)">
          <slot :name="`label-index-${index}`" :label="getLabel(option)">
            {{ getLabel(option) }}
          </slot>
        </p>
      </div>
    </div>

    <div class="native">
      <input
        v-for="(option, index) in options"
        :key="index"
        type="radio"
        :disabled="option.disabled"
        :name="name"
        :value="getLabel(option)"
        :checked="isSelectedNative(option)"
        @click="emitInput(option)"
      >
    </div>

    <slot name="under-input" />

  </div>
</template>

<script>
import { isEmptyObject } from '@convenia/helpers'
import CIconToggle from '@convenia/components/CIconToggle/CIconToggle'
import props from './props'

/**
 * A radio button component. When an option is selected, it emits an 'input'
 * event with the chosen option.
 */
export default {
  name: 'CRadioRaw',

  props,

  data () {
    return {
      selectedLabel: null,
    }
  },

  components: {
    CIconToggle
  },

  computed: {
    dataTestId () {
      const fieldName = this.fieldName || this.name
      return `radio-raw-${fieldName}`
    },

    hasSlots () {
      return Object.hasOwn(this, '$scopedSlots')
        ? !isEmptyObject(this.$scopedSlots)
        : !isEmptyObject(this.$slots)
    },

    classes () {
      return {
        '-disabled': this.disabled,
        '-has-slots': this.hasSlots
      }
    },

    style () {
      return {
        '--radio-columns-count': this.columns
      }
    },
  },

  methods: {
    isSelected (option) {
      if (this.trackBy && option instanceof Object) {
        return this.value instanceof Object
          ? this.value[this.trackBy] === option[this.trackBy] ? 'selected' : 'unselected'
          : this.value === option[this.trackBy] ? 'selected' : 'unselected'
      }

      return this.value === option
        ? 'selected'
        : 'unselected'
    },

    isSelectedNative (option) {
      return this.isSelected(option) === 'selected'
    },

    onToggleUpdate (option, isChecked) {
      if (isChecked) {
        this.emitInput(option)
      }
    },

    onClickLabel (option, index) {
      if (option.disabled) return

      const { iconToggleRefs } = this.$refs
      if (!iconToggleRefs || !Array.isArray(iconToggleRefs) || index >= iconToggleRefs.length) {
        return
      }

      const iconToggle = iconToggleRefs[index]
      if (iconToggle && iconToggle.$el) iconToggle.$el.click()
    },

    emitInput (option) {
      const value = this.trackBy && option instanceof Object && !(this.value instanceof Object)
        ? option[this.trackBy]
        : option
      /**
      * Return the value of option when selected
      */
      this.$emit('input', value)
    },

    getLabel (option) {
      if (this.displayBy && option instanceof Object) {
        return option[this.displayBy]
      }
      return option
    },

    handleHint (option, type) {
      if (!option.hint) return

      const eventName = `hint:${type}`

      if (type === 'show') this.$emit(eventName, option.hint)
      else if (type === 'hide') this.$emit(eventName)
    },

    handleSelectLabel (newLabel) {
      this.selectedLabel = newLabel
    },

    onMouseEnter (option) {
      this.handleSelectLabel(this.getLabel(option))
      this.handleHint(option, 'show')
    },

    onMouseLeave (option) {
      this.handleSelectLabel()
      this.handleHint(option, 'hide')
    }
  }
}
</script>

<style lang="scss">
%select {
  border-radius: 50%;
  transform: scale(1);
  transition: opacity .3s ease, transform .3s;
  background-color: color-var(disabled, dark);
}

.c-radio-raw {
  &.-disabled {
    pointer-events: none;
    user-select: none;

    & > .inner > .radio-option > .label { color: color-var(text, base-30); }
  }

  & > .text { margin-bottom: 20px; }

  & > .inner {
    display: grid;
    grid-column-gap: 10px;
    grid-template-columns: repeat(var(--radio-columns-count), 1fr);

    grid-row-gap: 10px;

    justify-content: space-around;

    & > .radio-option {
      display: flex;
      flex-flow: row;
      align-items: center;
      cursor: pointer;
      min-height: 0;

      &.-disabled {
        user-select: none;
        pointer-events: none;
        cursor: normal;

        & > .label { color: color-var(text, base-30); }
        & > .borderline {
          background-color: color-var(text, base-10);
          border: 0;
        }
      }

      & > .label {
        font-size: 14px;
        color: color-var(text, base-80);

        margin-left: 15px;

        html.ios.iphone & {
          line-height: 19px;
          min-height: 19px;
        }

        line-height: 20px;
        min-height: 20px;

        @include responsive (tablet, desktop) {
          line-height: 19px;
          margin-left: 20px;
          margin-top: 4px;
        }
      }
    }
  }

  & > .native {
    position: absolute;
    visibility: hidden;
  }

  & > .jumbo-validation { display: none; }

  &.-has-slots > .inner > .radio-option {
    align-items: center;

    & > .label {
      display: flex;
      align-items: center;
      margin-top: 0;
      flex: 1;
    }
  }
}
</style>
